<template lang="pug">
	.audio-tracker-track--canvas-image-drawer(v-if="drawerVisible")
		.canvas-image(v-if="canvasImage", :style="{ '--canvas-image': `url(${canvasImage})`, '--leftOffset': `${-1 * pps * (track.startOffset / playbackRate || 0)}px`, '--width': `${canvasWidth}px` }")
		.canvas-image(v-else, v-for="img in images", :style="{ '--canvas-image': `url(${img.value})`, '--leftOffset': `${(img.paintStart / img.pps - track.startOffset) * pps}px`, '--width': `${img.width / img.pps * pps}px` }")
</template>


<script>
	import _ from 'lodash';
	import audioHelper from './audio-helper';	

	export default {
		translationCategory: 'audioTracker.AudioTrackCanvasImageDrawer',
		name: 'audio-track-canvas-image-drawer',
		
		props: ['track', 'tracker', 'navigatorWidth', 'navigatorHeight'],
		data() {
			return {
				canvas: document.createElement('canvas'),
				skipLeft: 0,
				visibleCanvasWidth: 0,
				cachedParts: {},
				imagesWidth: 500,
				drawerVisible: false
			};
		},
		computed: {
			playbackRate() {
				return 1; //(track.playbackRate || 1) //is processing on buffer level
			},
			canvasImage() {
				return this.tracker?.tracksCanvasImages[this.track?.id];
			},
			trackBuffer() {
				return this.tracker.tracksBuffers[this.track?.id];
			},
			images() {
				let cp = this.getCachedPart();

				let images = cp.images;
				if (cp.total !== cp.images.length) {
					let downScale = this.getDownScale();
					if (downScale) {
						images = downScale.images;
					}
				}

				return (images || []).filter(f => ((f.paintEnd / f.pps - this.track.startOffset) * this.pps) > this.skipLeft && ((f.paintStart / f.pps - this.track.startOffset) * this.pps) < (this.skipLeft + this.visibleCanvasWidth));
			},
			canvasWidth() {
				return this.pps * this.track.getTrimmedDuration(true, true);
			},
			pps() {
				return this.tracker.display.pixelsPerSecond;
			},
			cachedPartsArr() {
				return Object.keys(this.cachedParts).map(f => ({
					pps: parseFloat(f),
					...this.cachedParts[f]
				}));
			},
			drawerBottom: {
				get: function() {
					return this.track.__trackLineTop + this.track.__trackLineHeight;
				},
				cache: false
			},
			drawerTop: {
				get: function() {
					return this.track.__trackLineTop;
				},
				cache: false
			},
			navTop: {
				get: function() {
					return this.tracker.$navigatorScrollOffset?.top || 0;
				},
				cache: false
			},
			navBottom: {
				get: function() {
					return this.navTop + this.navigatorHeight;
				},
				cache: false
			},
			drawerVisibleVertical: {
				get: function() {
					return this.drawerTop < this.navBottom && this.drawerBottom > this.navTop;
				},
				cache: false
			},
			aggTrack() {
				return this.track?.__aggregatedTrack;
			},
			normalizeMethodValue() {
				return this.aggTrack?.normalize?.method === 'lufs' ? (this.track?.__audioInfo?.momentaryMaxLUFS + 10) : this.track?.__audioInfo?.peakDb;
			},
			normalizeValue() {
				return this.aggTrack?.normalize?.method === 'lufs' ? (this.aggTrack?.normalize?.value + 10) : this.aggTrack?.normalize?.value;
			},
			heightCoef() {
				return this.aggTrack?.normalize?.enabled ? this.getDbCoef(this.normalizeValue) : this.getDbCoef(this.normalizeMethodValue || 0);//Math.max(0, this.getDbCoef(this.track?.__audioInfo?.peakDb) || 0) * 0.65;
			},
		},
		watch: {
			'trackBuffer'(v) {
				if (v) {
					if (__DEBUG__)
						console.log('Track buffer changed');
					this.cachedParts = {};
					this.$nextTick(() => this.draw());
					audioHelper.detectBeat(v, this.tracker.audioContext).then(results => {
						this.$set(this.track, '__bpm', results.bpm);
						this.$set(this.track, '__bpmInfo', results);
					});
				}
			},
			'navigatorScrollOffset': {
				handler() {
					this.$draw();
				},
				deep: true
			},
			'pps'() {
				this.$draw();
			},
			navigatorWidth() {
				this.$draw();
			},
			'track.__aggregatedTrack.normalize.enabled'() {
				this.cachedParts = {};
				this.$draw();
			},
			'track.__aggregatedTrack.normalize.value'() {
				this.cachedParts = {};
				this.$drawLong();
			},
			'track.playbackRate'() {
				this.cachedParts = {};
				this.$draw();
			},
			'track.insertion.JsonConfig.trackNumber'() {
				this.$draw();
			},
			'track.insertion.StartOffset'() {
				this.$draw();
			},
			'track.insertion.EndOffset'() {
				this.$draw();
			},
			drawerTop() {
				this.$draw();
			},
			drawerBottom() {
				this.$draw();
			},
			navTop() {
				this.$draw();
			},
			navigatorHeight() {
				this.$draw();
			},
			'track.__isSrcLoading'() {
				this.$draw();
			},
			heightCoef() {
				this.cachedParts = {};
				this.$draw();
			}
		},
		mounted() {
			if (__DEBUG__)
				window[`canvasImageDrawer_${this.track.__aggregatedTrack.name}_${Math.round(this.track.start)}`] = this;
			this.draw();
			this.$draw = _.debounce(this.draw, 20, {
				leading: false,
				trainling: true,
				maxWait: 500
			});

			this.$drawLong = _.debounce(this.draw, 500, {
				leading: false,
				trainling: true
			});

			this.$eventsBus.$on('navigatorScrollOffset', this.onNavigatorScrollOffset);
		},
		destroyed() {
			delete this.$draw;
			delete this.$drawLong;
			this.$eventsBus.$off('navigatorScrollOffset', this.onNavigatorScrollOffset);
		},
		methods: {
			getDbCoef(db) {
				let coef = (60 + db) / 60;
				return (1000 ** coef) / 1000;
			},
			onNavigatorScrollOffset(offset) {
				this.$draw();
				this.$forceUpdate();
			},
			getCachedPart() {
				let keys = Object.keys(this.cachedParts);
				let existingKey = keys.find(f => Math.abs(1 - parseFloat(f) / this.pps) < 0.1);
				if (!existingKey) {
					existingKey = this.pps;
					this.$set(this.cachedParts, this.pps, {
						total: 0,
						images: []
					});
				}
				return this.cachedParts[existingKey];
			},
			getDownScale(pps) {
				pps = this.pps;
				return this.cachedPartsArr.find(f => f.total && f.total === f.images.length && ((Math.abs(1 - f.pps / pps) < 0.1 || f.pps > pps)));
			},
			async calcCachedImage(paintStart, paintEnd) {
				const images = this.getCachedPart().images;
				let image = images.find(f => f.paintStart === paintStart);

				if (this.getDownScale()) return;

				if (image) return image;

				image = {
					paintStart,
					paintEnd,
					pps: this.pps,
					value: null,
					width: Math.floor(paintEnd - paintStart) + 1
				};
				
				images.push(image);

				image.value = await this.drawImage(paintStart, paintEnd);

				return image;
			},
			async drawImage(paintStart, paintEnd) {
				let canvasObj = document.createElement('canvas');
				let buffer = this.track.__buffer;
				let context = canvasObj.getContext('2d');

				if (!buffer?.length) return;

				const leftChannel = this.tracker.getBufferChannelData(buffer, 0);
				
				const trackDurationWithoutOffsets = this.track.getTrimmedDuration(true, true);
				const realCanvasWidth = trackDurationWithoutOffsets * this.pps;
				const drawLines = parseInt(realCanvasWidth);
				const totallength = leftChannel.length;
				const detalization = 10;
				const lineGap = (realCanvasWidth / drawLines) / detalization;
				const eachBlock = Math.floor(totallength / drawLines) / detalization;

				const canvasScale = 2;
				const imageHeight = 140;
				canvasObj.width = (paintEnd - paintStart) * canvasScale;
				canvasObj.height = imageHeight * canvasScale;

				context.scale(canvasScale, canvasScale);

				context.save();
				context.fillStyle = '#dedede';
				context.fillRect(0, 0, (paintEnd - paintStart), imageHeight);
				context.strokeStyle = '#f27649';
				context.translate(0, imageHeight / 2);
				context.lineWidth = 0.25;

				let coords = [];

				const getValue = (ch) => {
					const block = Math.max(Math.min(200, eachBlock), 20);
					const blocks = [];
					for(let i = 0; i < ch.length; i += block) {
						blocks.push(_.sum(ch.slice(0, i - 1)) / block);
					}
					return _.sum(blocks) / blocks.length;
				}

				for(var k = paintStart; k <= paintEnd; k += lineGap) {
					let start = Math.floor(parseFloat(k / realCanvasWidth) * parseFloat(totallength));
					let end = start + eachBlock;
					if (start > totallength) break;
					const value = getValue(leftChannel.slice(start, end));
					var x = Math.min(paintEnd, k) - paintStart;
					var y1 = value * (imageHeight / 1) * 1.5 || 0.1;

					coords.push([x,y1]);
				}

				var grad= context.createLinearGradient(0, 25, 0, -25);
				grad.addColorStop(0, '#fd2f2f');
				grad.addColorStop(0.5, '#ff4910');
				grad.addColorStop(1, '#ffa300');

				const maxY = _.max(coords.map(f => Math.abs(f[1])));

				coords.map((c, i) => {
					const last = coords[i-1];
					if (i > 0) {
						context.lineWidth = 0.25;
						context.strokeStyle = grad;
						context.beginPath();
						context.moveTo(last[0], last[1] / maxY * 0.5 * imageHeight * this.heightCoef);
						context.lineTo(c[0], c[1] / maxY * 0.5 * imageHeight * this.heightCoef);
						context.stroke();
					}
				});

				return canvasObj.toDataURL('image/jpeg', 0.35);//.toDataURL('image/png');
			},
			async draw() {
				if (this.tracker.isPublishMode) return;
				if (!this.heightCoef) return;

				let canvasObj = this.canvas;
				let buffer = this.track.__buffer;
				let hasAudio = buffer?.length;
				let track = this.track;

				if (track) {
					var trackDuration = track.getTrimmedDuration(void 0, true);
					var canvasWidth = trackDuration * this.pps;
					var realCanvasWidth = track.getTrimmedDuration(true, true) * this.pps;
					var navigatorWidth = this.tracker.getNavigatorWidth(10);

					track.__canvasWidth = canvasWidth;
					track.__canvasLeft = track.start * this.pps;

					if (!canvasObj) return;

					if (track.__customCanvasDraw) {
						const res = await track.__customCanvasDraw();
						if (res?.base64)
							this.$set(this.tracker.tracksCanvasImages, track.id, res?.base64);
						return;
					}

					if (track.__isAudioTrack && hasAudio && track.__buffer/* && (!this.tracker.tracksCanvasImages[track.id])*/) {
						const trackStart = track.start * this.pps;
						const trackEnd = (track.start + track.getTrimmedDuration(void 0, true)) * this.pps;
						const navStart = this.tracker.$navigatorScrollOffset?.left || 0;
						const skipLeft = navStart > trackStart ? (navStart - trackStart) : 0;
						
						const visibleCanvasWidth = (trackEnd > (navStart + navigatorWidth) ? ((navStart + navigatorWidth) - trackStart) : canvasWidth) - skipLeft;

						this.drawerVisible = this.drawerVisibleVertical;

						if (visibleCanvasWidth < 0 || !this.drawerVisibleVertical) return;

						this.skipLeft = skipLeft;
						this.visibleCanvasWidth = Math.min(navigatorWidth, visibleCanvasWidth);

						const paintStart = skipLeft;
						const paintEnd = Math.min(Math.min(navigatorWidth, visibleCanvasWidth) + paintStart + (this.tracker.isPlaying ? navigatorWidth : 0), canvasWidth);

						let total = 0;

						let offset = track.startOffset * this.pps;

						for(let i = 0; i < (canvasWidth + offset); i += this.imagesWidth) {
							if ((i + this.imagesWidth - 1 - offset) >= 0) {
								total++;
								if (i + this.imagesWidth - offset > this.skipLeft && i - offset < (this.skipLeft + this.visibleCanvasWidth)) {
									this.calcCachedImage(i, Math.min(realCanvasWidth, i + this.imagesWidth - 1));
								}
							}
						}

						this.getCachedPart().total = total;
					}
				}
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-track--canvas-image-drawer {
		@import "main/themes/generated/variables.base.scss";

		position: absolute;
		left: -1px;
		top: -2px;
		right: 0px;
		bottom: 0px;
		overflow: hidden;
		background: #e0e0e0aa;

		> .canvas-image {
			background: var(--canvas-image);
			background-size: 100% 100%;
			position: absolute;
			left: var(--leftOffset);
			top: 0px;
			width: var(--width);
			bottom: 0px;
		}
	}
</style>
